/**
 * ONNX 깊이 추정 워커.
 *
 * 워커 소스를 **문자열로 들고 있다가 Blob URL로 띄운다.** 번들러에 워커 처리를
 * 맡기지 않는 이유는 하나다 — transformers.js는 무겁고(sharp, onnxruntime-node를
 * 딸려 온다) 선택 기능이다. CDN에서 동적 import 하면 `bun install`이 가벼워지고,
 * 이 기능을 안 쓰는 사람은 한 바이트도 받지 않는다.
 *
 * 번들에 넣고 싶다면 README의 "오프라인 모델" 절을 보라.
 */

/** 워커 안에서 실행될 코드. 템플릿 리터럴을 쓰지 않는다 — 바깥 리터럴과 충돌한다. */
const WORKER_SOURCE = `
let cached = null;

function post(msg, transfer) {
  self.postMessage(msg, transfer || []);
}

async function getPipeline(cfg) {
  if (cached && cached.key === cfg.modelId + '|' + cfg.dtype + '|' + cfg.device) {
    return cached.value;
  }
  const mod = await import(cfg.cdn);
  mod.env.allowLocalModels = false;
  mod.env.allowRemoteModels = true;

  let device = cfg.device;
  if (device === 'auto') {
    device = (typeof navigator !== 'undefined' && navigator.gpu) ? 'webgpu' : 'wasm';
  }

  const load = async (dev) => mod.pipeline('depth-estimation', cfg.modelId, {
    device: dev,
    dtype: cfg.dtype,
    progress_callback: (p) => {
      post({
        type: 'progress',
        phase: p.status || 'loading',
        file: p.file,
        ratio: typeof p.progress === 'number' ? p.progress / 100 : undefined,
      });
    },
  });

  let pipe;
  try {
    pipe = await load(device);
  } catch (err) {
    if (device === 'webgpu') {
      post({ type: 'progress', phase: 'webgpu-fallback' });
      device = 'wasm';
      pipe = await load('wasm');
    } else {
      throw err;
    }
  }

  const value = { pipe: pipe, RawImage: mod.RawImage, device: device };
  cached = { key: cfg.modelId + '|' + cfg.dtype + '|' + cfg.device, value: value };
  return value;
}

function bilinear(src, sw, sh, dw, dh) {
  const out = new Float32Array(dw * dh);
  for (let y = 0; y < dh; y++) {
    const fy = Math.min(sh - 1, Math.max(0, ((y + 0.5) * sh) / dh - 0.5));
    const y0 = Math.floor(fy);
    const y1 = Math.min(sh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < dw; x++) {
      const fx = Math.min(sw - 1, Math.max(0, ((x + 0.5) * sw) / dw - 0.5));
      const x0 = Math.floor(fx);
      const x1 = Math.min(sw - 1, x0 + 1);
      const tx = fx - x0;
      const a = src[y0 * sw + x0] + (src[y0 * sw + x1] - src[y0 * sw + x0]) * tx;
      const b = src[y1 * sw + x0] + (src[y1 * sw + x1] - src[y1 * sw + x0]) * tx;
      out[y * dw + x] = a + (b - a) * ty;
    }
  }
  return out;
}

self.onmessage = async (event) => {
  const req = event.data;
  const started = performance.now();
  try {
    const ctx = await getPipeline(req);
    const rgba = new Uint8ClampedArray(req.buffer);
    const px = req.width * req.height;

    // depth-estimation 전처리기는 RGB 3채널을 기대한다.
    const rgb = new Uint8ClampedArray(px * 3);
    for (let i = 0, s = 0, d = 0; i < px; i++, s += 4, d += 3) {
      rgb[d] = rgba[s];
      rgb[d + 1] = rgba[s + 1];
      rgb[d + 2] = rgba[s + 2];
    }
    const image = new ctx.RawImage(rgb, req.width, req.height, 3);

    post({ type: 'progress', phase: 'inference' });
    const result = await ctx.pipe(image);

    // predicted_depth는 float 텐서라 uint8 depth 이미지보다 계조가 훨씬 곱다.
    let src, sw, sh;
    const t = result.predicted_depth;
    if (t && t.data && t.dims) {
      const dims = t.dims;
      sh = dims[dims.length - 2];
      sw = dims[dims.length - 1];
      src = Float32Array.from(t.data);
    } else if (result.depth && result.depth.data) {
      sw = result.depth.width;
      sh = result.depth.height;
      src = Float32Array.from(result.depth.data);
    } else {
      throw new Error('모델이 깊이 텐서를 돌려주지 않았습니다.');
    }

    const resized = bilinear(src, sw, sh, req.width, req.height);

    // Depth Anything은 역깊이를 낸다 — 값이 클수록 카메라에 가깝다. 그대로 변위에 쓴다.
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < resized.length; i++) {
      const v = resized[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    const span = hi - lo || 1;
    for (let i = 0; i < resized.length; i++) resized[i] = (resized[i] - lo) / span;

    post({
      type: 'result',
      width: req.width,
      height: req.height,
      buffer: resized.buffer,
      device: ctx.device,
      ms: performance.now() - started,
    }, [resized.buffer]);
  } catch (err) {
    post({ type: 'error', message: (err && err.message) ? err.message : String(err) });
  }
};
`;

let blobUrl: string | null = null;

export function createDepthWorker(): Worker {
  if (!blobUrl) {
    blobUrl = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
  }
  return new Worker(blobUrl, { type: 'module' });
}
